/**
 * Utility functions for cart calculations that can be shared
 * between MiniCart and Cart pages.
 */

/**
 * Calculate the base price for a cart item, handling various price structures
 *
 * @param item The cart item object
 * @returns The base price as a number
 */
export const calculateBasePrice = (item: any): number => {
  // Direct price value if available
  if (typeof item.price === "number") {
    return item.price;
  }

  // Product price structure
  if (item.product?.price) {
    // Prefer discount price if available
    if (item.product.price.discount?.newPrice !== undefined) {
      return Number(item.product.price.discount.newPrice);
    }

    // Fall back to regular price
    if (item.product.price.regPrice !== undefined) {
      return Number(item.product.price.regPrice);
    }
  }

  // Last resort fallback
  return 0;
};

/**
 * Calculate surcharge from customizable options
 *
 * @param item The cart item object
 * @returns The total surcharge as a number
 */
export const calculateOptionsSurcharge = (item: any): number => {
  return (item.customizableOptions || []).reduce(
    (acc: number, option: any) => acc + (Number(option.priceSurcharge) || 0),
    0
  );
};

/**
 * Calculate total price for a cart item (base + surcharge) * quantity
 *
 * @param item The cart item object
 * @returns The total line price
 */
export const calculateLineTotal = (item: any): number => {
  const basePrice = calculateBasePrice(item);
  const surcharge = calculateOptionsSurcharge(item);
  return (basePrice + surcharge) * item.quantity;
};

/**
 * Calculate subtotal for an array of cart items, including optimistic ones
 * Updated to use clean pricing structure with legacy fallback
 *
 * @param items Array of cart items
 * @returns The total price
 */
export const calculateItemsSubtotal = (items: any[]): number => {
  if (!items || items.length === 0) return 0;

  return items.reduce((total, item) => {
    if (item.quantity <= 0) return total;

    // Phase 3: Use clean pricing structure with legacy fallback
    let basePrice = 0;
    let surcharge = 0;

    // Try clean pricing structure first (current system)
    if (item.cleanPricing?.pricing) {
      // Use original price for subtotal calculation (before discounts)
      basePrice = item.cleanPricing.pricing.originalUnitPrice || 0;
      surcharge = item.cleanPricing.pricing.customizationSurcharge || 0;
    }
    // Try item-level pricing structure (direct pricing data)
    else if (item.pricing) {
      basePrice = item.pricing.originalUnitPrice || 0;
      surcharge = item.pricing.customizationSurcharge || 0;
    }
    // Fallback to legacy pricing structure
    else {
      basePrice = calculateBasePrice(item);
      surcharge = calculateOptionsSurcharge(item);
    }

    const itemTotal = (basePrice + surcharge) * item.quantity;
    return total + itemTotal;
  }, 0);
};

/**
 * Filters out items with zero quantity and returns a new array
 * Useful for calculating totals when items are being removed
 *
 * @param items Array of cart items
 * @returns Filtered array with non-zero quantities
 */
export const filterNonZeroItems = (items: any[]): any[] => {
  if (!items || items.length === 0) return [];
  return items.filter((item) => item.quantity > 0);
};

/**
 * Creates an optimistic version of a cart state update
 * (Can be used when adding/removing items to immediately update UI)
 *
 * @param items Current items array
 * @param updatedItem The item being updated
 * @param newQuantity The new quantity for the item
 * @returns New array with optimistic update applied
 */
export const createOptimisticUpdate = (
  items: any[],
  updatedItem: any,
  newQuantity: number
): any[] => {
  // For removal, filter out the item
  if (newQuantity <= 0) {
    return items.filter(
      (item) => item.cartCustomId !== updatedItem.cartCustomId
    );
  }

  // For update, find and modify the item
  return items.map((item) => {
    if (item.cartCustomId === updatedItem.cartCustomId) {
      return {
        ...item,
        quantity: newQuantity,
        isOptimisticChange: true,
      };
    }
    return item;
  });
};

/**
 * Check if cart items have any promotional discounts applied
 *
 * @param items Array of cart items
 * @returns Boolean indicating if promotional discounts exist
 */
export const hasPromotionalDiscounts = (items: any[]): boolean => {
  if (!items || items.length === 0) return false;

  return items.some((item) => {
    if (item.quantity <= 0) return false;

    // Check clean pricing structure for promotional discounts
    const promotionalDiscount =
      item.cleanPricing?.pricing?.discounts?.promotional?.totalAmount ||
      item.pricing?.discounts?.promotional?.totalAmount ||
      0;

    return promotionalDiscount > 0;
  });
};
